import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { walletService, type WalletData } from '../services/walletService'

interface WalletContextType {
  walletData: WalletData | null
  isLoadingWallet: boolean
  refreshWallet: () => Promise<void>
  formatCurrency: (amount: number) => string
}

const WalletContext = createContext<WalletContextType | undefined>(undefined)

interface WalletProviderProps {
  children: ReactNode
}

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth()
  const [walletData, setWalletData] = useState<WalletData | null>(null)
  const [isLoadingWallet, setIsLoadingWallet] = useState(false)

  // Load wallet data
  useEffect(() => {
    if (isAuthenticated && user) {
      loadWalletData()
    }
  }, [isAuthenticated, user])

  // Listen for balance notifications from WebSocket
  useEffect(() => {
    const handleBalanceNotification = (event: CustomEvent) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('💰 Received balance notification in WalletContext:', event.detail)
      }
      // Reload wallet data when balance is adjusted
      loadWalletData()
    }

    // Listen for custom events from WebSocket
    window.addEventListener('balance_notification', handleBalanceNotification as EventListener)

    return () => {
      window.removeEventListener('balance_notification', handleBalanceNotification as EventListener)
    }
  }, [])

  const loadWalletData = async () => {
    try {
      setIsLoadingWallet(true)
      const response = await walletService.getWallet()
      
      if (response.success && response.data) {
        setWalletData(response.data)
      } else if (process.env.NODE_ENV === 'development') {
        console.error('❌ Failed to load wallet:', response.error)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Wallet loading error:', error)
      }
    } finally {
      setIsLoadingWallet(false)
    }
  }

  const refreshWallet = async () => {
    await loadWalletData()
  }

  const formatCurrency = (amount: number) => {
    return walletService.formatCurrency(amount)
  }

  const value: WalletContextType = {
    walletData,
    isLoadingWallet,
    refreshWallet,
    formatCurrency
  }

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  )
}

export const useWallet = (): WalletContextType => {
  const context = useContext(WalletContext)
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider')
  }
  return context
}
